import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BlockComponentFactory } from '../BlockFactory'
import { useBlockEngine } from '../BlockEngineContext'
import { useBlockListeners, useBlockMethod } from '../BlockHooks'
import { LabelBlock } from './LabelBlock'
import { HTMLBlockProps } from './block-props.types'
import { LABEL_BOLD_FONT_STYLE } from '../../constants'
import { toCSSSize } from './block-utils'
import {
    FTS_HTML_EVENT_DISPATCH,
    FTS_HTML_EVENT_DOWNLOAD,
    FTS_HTML_EVENT_ERROR,
    FTS_HTML_EVENT_READY,
    HTML_IFRAME_ALLOW,
    HTML_IFRAME_SANDBOX,
    downloadPublicFile,
    parseIframeMessage,
    wrapFtsPostMessage,
    wrapUserHtml,
} from './htmlBlock.helpers'
import { Logger } from '../../core/Logger'
import classes from './HTMLBlock.module.less'

const DEFAULT_FRAME_HEIGHT = '650px'

export const HTMLBlockComponent = BlockComponentFactory.createBlockComponent<HTMLBlockProps>(
    props => {
        const {
            blockId,
            label,
            infoText,
            value,
            width,
            height,
            visibility = 'visible',
            disabled = false,
            style,
            onEvent: onEventProp,
            iframeSandbox = HTML_IFRAME_SANDBOX,
        } = props

        const isEditable = useBlockEngine().blockEngine.getEditableSignal().value
        const iframeDisabled = !isEditable || disabled

        const iframeRef = useRef<HTMLIFrameElement>(null)
        const pendingMessagesRef = useRef<unknown[]>([])
        const previousHtmlRef = useRef<string | undefined>(undefined)
        const frameReadyRef = useRef(false)

        const [onEventListener] = useBlockListeners<(value: unknown) => void>('onEvent')
        const onEvent = onEventProp ?? onEventListener?.value

        const [frameReady, setFrameReady] = useState(false)

        const deliverToIframe = useCallback((message: unknown) => {
            iframeRef.current?.contentWindow?.postMessage(wrapFtsPostMessage(message), '*')
        }, [])

        const handleFrameReady = useCallback(
            (ready: boolean) => {
                frameReadyRef.current = ready
                setFrameReady(ready)
                if (!ready) {
                    pendingMessagesRef.current = []
                    return
                }

                const pending = pendingMessagesRef.current
                pendingMessagesRef.current = []
                pending.forEach(message => deliverToIframe(message))
            },
            [deliverToIframe]
        )

        const postMessage = useCallback(
            (message: unknown) => {
                if (!frameReadyRef.current) {
                    pendingMessagesRef.current.push(message)
                    return
                }
                deliverToIframe(message)
            },
            [deliverToIframe]
        )

        useBlockMethod('postMessage', postMessage)

        const trimmedHtml = value?.trim() ?? ''
        const srcDoc = useMemo(() => (trimmedHtml ? wrapUserHtml(trimmedHtml) : ''), [trimmedHtml])

        useEffect(() => {
            const htmlChanged =
                previousHtmlRef.current !== undefined && previousHtmlRef.current !== trimmedHtml
            previousHtmlRef.current = trimmedHtml

            if (!htmlChanged) return

            handleFrameReady(false)
        }, [trimmedHtml, handleFrameReady])

        useEffect(() => {
            const onWindowMessage = (event: MessageEvent) => {
                const frameWin = iframeRef.current?.contentWindow
                if (!frameWin || event.source !== frameWin) return

                const msg = parseIframeMessage(event.data)
                if (!msg) return

                if (msg.type === FTS_HTML_EVENT_READY) {
                    handleFrameReady(true)
                    return
                }

                if (msg.type === FTS_HTML_EVENT_ERROR) {
                    Logger.getInstance().error(msg.message, {
                        source: 'HTMLBlock',
                        context: { blockId, label },
                    })
                    return
                }

                if (msg.type === FTS_HTML_EVENT_DISPATCH) {
                    if (onEvent) {
                        onEvent(msg.value)
                        return
                    }
                    Logger.getInstance().warn(
                        'HTML block event dropped: no onEvent listener attached',
                        {
                            source: 'HTMLBlock',
                            context: { blockId, label },
                        }
                    )
                    return
                }

                if (msg.type === FTS_HTML_EVENT_DOWNLOAD) {
                    downloadPublicFile(msg.url, msg.filename).catch(error => {
                        Logger.getInstance().error(
                            error instanceof Error ? error.message : 'Failed to download file',
                            { source: 'HTMLBlock', context: { blockId, label } }
                        )
                    })
                }
            }

            window.addEventListener('message', onWindowMessage)
            return () => window.removeEventListener('message', onWindowMessage)
        }, [blockId, handleFrameReady, label, onEvent])

        useEffect(() => {
            if (!frameReady) return

            deliverToIframe({
                label,
                infoText,
                width,
                height,
                visibility,
                disabled: iframeDisabled,
            })
        }, [
            frameReady,
            label,
            infoText,
            width,
            height,
            visibility,
            iframeDisabled,
            deliverToIframe,
        ])

        const frameHeight = toCSSSize(height) || DEFAULT_FRAME_HEIGHT
        const containerWidth = toCSSSize(width) || '100%'
        const containerStyle = useMemo(
            () => ({
                ...style,
                display: visibility === 'hidden' ? 'none' : 'flex',
                flexDirection: 'column' as const,
                gap: 8,
                width: containerWidth,
                opacity: disabled ? 0.5 : 1,
            }),
            [containerWidth, disabled, style, visibility]
        )

        return (
            <div style={containerStyle}>
                <LabelBlock label={label} infoText={infoText} labelStyle={LABEL_BOLD_FONT_STYLE} />
                {trimmedHtml ? (
                    <iframe
                        key={trimmedHtml}
                        ref={iframeRef}
                        title={label || 'HTML'}
                        className={classes.iframe}
                        srcDoc={srcDoc}
                        allow={HTML_IFRAME_ALLOW}
                        style={{ height: frameHeight }}
                        sandbox={iframeSandbox.trim()}
                    />
                ) : (
                    <div className={classes.empty}>Add HTML in the properties panel</div>
                )}
            </div>
        )
    }
)
